<template>
  <div class="register-page-wrapper">
    <div class="register-container">
      <div class="main-content">
        <div class="register-wrapper">
          <div class="register-form-container">
            <el-card class="register-card" shadow="always">
              <!-- 关闭按钮 -->
              <el-icon class="close-btn" @click="goBack">
                <Close />
              </el-icon>

              <div class="register-body">
                <div class="register-form-wrapper">
                  <!-- 顶部注册提示 -->
                  <div class="register-header">
                    <h3>微信扫码注册</h3>
                  </div>

                  <!-- 微信注册容器 -->
                  <div class="wechat-register-container">
                    <div v-if="wechatQrCodeUrl" class="wechat-qrcode">
                      <img :src="wechatQrCodeUrl" alt="微信注册二维码">
                    </div>
                    <div v-else class="loading-qrcode">
                      正在生成二维码...
                    </div>
                  </div>

                  <!-- 登录链接 -->
                  <div class="login-link">
                    <span>已有账号？</span>
                    <el-link type="primary" @click="navigateToLogin" style="font-style: italic;">去登录</el-link>
                  </div>

                  <!-- 用户协议和隐私政策 -->
                  <div class="agreement">
                    注册即表示同意
                    <el-link type="primary" @click="showTermsDialog">《用户协议》</el-link>
                    和
                    <el-link type="primary" @click="showPrivacyDialog">《隐私政策》</el-link>
                  </div>
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部版权信息 -->
    <el-footer class="register-footer" height="auto">
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-sections">
            <!-- 网站介绍 -->
            <div class="footer-section">
              <div class="footer-logo">

              </div>
              <p class="footer-desc">
                专业的印前设计工具，让设计更加高效精准。我们致力于为各行业提供最优质的印前解决方案。
              </p>
            </div>

            <!-- 联系方式 -->
            <div class="footer-section">
              <h4 class="section-title">联系方式</h4>
              <div class="contact-info">
                <div class="contact-item">
                  <el-icon><Message /></el-icon>
                  <span>3052594895@qq.com</span>
                </div>
                <div class="contact-item">
                  <el-icon><Phone /></el-icon>
                  <span>18925330037</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部版权信息 -->
          <div class="footer-bottom">
            <div class="copyright-section">
              <div class="copyright-text">
                © 2025 中山市易印网络科技有限公司.保留所有权利.
              </div>

            </div>
            <div class="certification-info">
              <div class="beian-container">
                <img src="../../assets/police.gif" alt="公安备案图标" class="beian-icon">
                <a href="http://www.beian.gov.cn" target="_blank" class="beian-link">
                  粤公网安备 44200002443702号
                </a>
                <span class="divider">|</span>
                <a href="https://beian.miit.gov.cn/" target="_blank" class="icp-link">
                  粤ICP备16014837号
                </a>
              </div>
              <div class="legal-links">
                <a href="/privacyPolicy" @click.prevent="showPrivacyDialog" class="legal-link">隐私政策</a>
                <span class="divider">|</span>
                <a href="/userAgreement" @click.prevent="showTermsDialog" class="legal-link">用户协议</a>
                <span class="divider">|</span>
                <a class="legal-link">法律声明</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-footer>
  </div>

  <!-- 服务条款弹窗 -->
  <el-dialog
      v-model="termsDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
  >
    <template #header>

    </template>
    <div class="dialog-content" v-html="termsContent"></div>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="termsDialogVisible = false">关闭</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 隐私政策弹窗 -->
  <el-dialog
      v-model="privacyDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
  >
    <template #header>

    </template>
    <div class="dialog-content" v-html="privacyContent"></div>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="privacyDialogVisible = false">关闭</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 绑定手机弹窗 -->
  <BindPhoneDialog
      v-model="bindPhoneDialogVisible"
      :session-id="currentSessionId"
      @bind-success="handleBindSuccess"
  />
</template>

<script setup>
import { ref, onMounted, onUnmounted  } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { eventBus } from '@/utils/eventBus'
import BindPhoneDialog from '@/components/BindPhoneDialog.vue'

const router = useRouter()

// 微信注册相关
const wechatQrCodeUrl = ref('')
const currentSessionId = ref('')
const bindPhoneDialogVisible = ref(false)
let pollingTimer = null

// 新增：二维码过期相关变量
const isQrCodeExpired = ref(false)
const qrCodeCreateTime = ref(null)
let expireTimer = null

// 弹窗控制变量
const termsDialogVisible = ref(false)
const privacyDialogVisible = ref(false)

// 用户协议内容
const termsContent = ref(`
  <h2>易比图工具用户协议</h2>
  <p><strong>版本日期：2025年8月26日</strong></p>
  <p>本协议是您（以下简称"用户"）与易印公司（以下简称"本公司"）就使用易比图工具（以下简称"本软件"）服务所订立的协议。请您在注册、下载、安装或使用本软件及相关服务前，仔细阅读并充分理解本协议。</p>

  <h3>1. 服务内容</h3>
  <ul>
    <li>本软件提供屏幕截图图片比对与差异显示功能，主要用于包装印刷等视觉校对场景。</li>
    <li>本软件的比对结果仅供参考，最终决定应由用户自行或由专业人员确认。</li>
  </ul>

  <h3>2. 使用规范</h3>
  <ul>
    <li>用户不得将本软件用于任何违法、侵权或未经授权的用途。</li>
    <li>用户在使用本软件过程中产生的所有行为及后果，由用户自行承担。</li>
    <li>用户应自行做好数据备份和安全防护。</li>
  </ul>

  <h3>3. 知识产权</h3>
  <ul>
    <li>本软件及其全部内容，包括但不限于代码、界面设计、图标（含SVG图标）、文字、图像等，均受著作权法及相关法律保护，归本公司所有。</li>
    <li>未经本公司书面许可，用户不得复制、修改、出售、反向工程或以其他方式使用本软件的全部或部分内容。</li>
  </ul>

  <h3>4. 免责声明与责任限制</h3>
  <ul>
    <li>因用户依赖比对结果造成的任何直接或间接损失，本公司不承担责任。</li>
    <li>因不可抗力（包括但不限于自然灾害、网络故障、系统故障）导致的服务中断或数据丢失，本公司不承担责任。</li>
  </ul>

  <h3>5. 服务变更与终止</h3>
  <ul>
    <li>本公司保留随时修改、暂停或终止全部或部分服务的权利，并将在官网公告或通过适当方式提前通知用户。</li>
    <li>用户有权随时停止使用本软件。</li>
  </ul>

  <h3>6. 法律适用与争议解决</h3>
  <ul>
    <li>本协议的签订、履行、解释及争议解决均适用中华人民共和国法律。</li>
    <li>双方因本协议产生的争议，应友好协商解决；协商不成的，应提交至本公司所在地人民法院诉讼解决。</li>
  </ul>
`)
// 隐私政策内容
const privacyContent = ref(`
  <h2>易比图工具隐私政策</h2>
  <p><strong>版本日期：2025年8月26日</strong></p>
  <p>本隐私政策旨在说明本公司在您使用本软件及相关服务过程中，如何收集、使用、存储和保护您的个人信息。</p>

  <h3>1. 信息收集</h3>
  <ul>
    <li><strong>注册信息</strong>：包括姓名、邮箱、联系电话等（如适用）。</li>
    <li><strong>使用数据</strong>：包括软件功能使用频率、比对任务类型、错误日志（不含比对图片内容本身）。</li>
    <li><strong>设备信息</strong>：包括操作系统类型、版本号、硬件型号等。</li>
  </ul>

  <h3>2. 信息使用</h3>
  <ul>
    <li>提供、维护和优化本软件的功能与性能。</li>
    <li>为用户提供技术支持与更新通知。</li>
    <li>在用户同意的情况下，用于发送产品资讯或营销活动信息。</li>
  </ul>

  <h3>3. 信息存储与保护</h3>
  <ul>
    <li>本公司采取符合行业标准的安全措施保护用户数据，防止未经授权的访问、披露、篡改或毁坏。</li>
    <li>除法律法规另有规定外，本公司仅在为实现本政策所述目的所必需的期限内保存用户信息。</li>
  </ul>

  <h3>4. 信息共享</h3>
  <ul>
    <li>本公司不会向第三方出售或出租用户信息。</li>
    <li>仅在以下情况下共享用户信息：
      <ul>
        <li>获得用户事先同意；</li>
        <li>法律法规或政府机关要求；</li>
        <li>为维护本公司合法权益所必需。</li>
      </ul>
    </li>
  </ul>

  <h3>5. 用户权利</h3>
  <ul>
    <li>用户可随时访问、更正或删除其个人信息（法律法规另有规定的除外）。</li>
    <li>用户可撤回同意，但可能会影响某些功能的使用。</li>
  </ul>

  <h3>6. 政策更新</h3>
  <ul>
    <li>本公司可能会不时更新本政策，并通过官网或其他适当方式通知用户。</li>
    <li>更新后的政策自发布之日起生效。</li>
  </ul>
`)

// 显示对话框的方法
const showTermsDialog = () => {
  termsDialogVisible.value = true
}

const showPrivacyDialog = () => {
  privacyDialogVisible.value = true
}

// 返回上一页
const goBack = () => {
  clearPolling() // 离开页面时清理轮询
  router.go(-1)
}

// 跳转到登录页
const navigateToLogin = () => {
  clearPolling() // 离开页面时清理轮询
  router.push('/login')
}

// 清理轮询函数
const clearPolling = () => {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
}

// 生成微信注册二维码
const generateWechatQrCode = async () => {
  try {
    const response = await request.get('/auth/wechat/mp-qrcode')
    const { qrUrl, sessionId } = response.data

    wechatQrCodeUrl.value = qrUrl
    currentSessionId.value = sessionId

    // 启动轮询检查注册状态
    startPolling()
  } catch (error) {
    // ElMessage.error('获取二维码失败')
  }
}

// 启动轮询
const startPolling = () => {
  clearPolling()

  const poll = async () => {
    try {
      const statusRes = await request.get('/auth/wechat/login/status', {
        params: { sessionId: currentSessionId.value }
      })

      const responseData = statusRes.data || {}

      if (responseData.status === 'success') {
        const token = responseData.token
        const hasBoundPhone = responseData.hasBoundPhone

        // 保存token到localStorage
        localStorage.setItem('auth_token', token)
        console.log('注册Token存储完成:', localStorage.getItem('auth_token'));

        // 触发登录事件
        eventBus.emitLogin()

        // 等待用户信息加载完成
        await new Promise(resolve => setTimeout(resolve, 500));

        if (hasBoundPhone) {
          ElMessage.success('微信注册成功')
          clearPolling()
          router.push('/home')
        } else {
          bindPhoneDialogVisible.value = true
        }
    } else if (responseData.status === 'waiting_bind') {
        // 等待绑定手机的状态，显示绑定弹窗
        bindPhoneDialogVisible.value = true
      } else if (responseData.status === 'expired') {
        ElMessage.warning('二维码已过期，正在重新生成...')
        generateWechatQrCode()
      } else if (responseData.status === 'waiting') {
        pollingTimer = setTimeout(poll, 2000)
      }
    } catch (error) {
      console.error('轮询错误:', error)
      pollingTimer = setTimeout(poll, 2000)
    }
  }

  pollingTimer = setTimeout(poll, 2000)
}

// 处理绑定成功
const handleBindSuccess = () => {
  clearPolling()

  // 触发登录事件
  eventBus.emitLogin()

  // 等待用户信息加载完成后再跳转
  setTimeout(() => {
    ElMessage.success('注册成功！')
    router.push('/home')
  }, 500);
}

onMounted(() => {
  generateWechatQrCode()
})


onUnmounted(() => {
  clearPolling()
})
</script>

<style scoped lang="scss">

/* 错误信息样式 */
.error-message {
  display: flex;
  align-items: center;
  color: #f84949;
  font-size: 12px;
  margin-top: 4px;
  height: 20px; /* 固定高度 */
  opacity: 1;
  transition: opacity 0.3s ease;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

/* 成功信息样式 */
.success-message {
  display: flex;
  align-items: center;
  color: #67c23a;
  font-size: 12px;
  margin-top: 4px;
  height: 20px; /* 固定高度 */
  opacity: 1;
  transition: opacity 0.3s ease;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.register-page-wrapper {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: fixed;
}

.register-container {
  width: 100%;
  height: 65vh;
  background: url('@/assets/login/lr-bg.gif') ;
  background-size: cover;
  position: relative;
  overflow: hidden;
}

.main-content {
  display: flex;
  flex-direction: column;
  justify-content: center; // 垂直居中
  align-items: center;
  width: 100%;
  margin-top: 0; // 移除顶部边距
}

.register-wrapper {
  display: flex;
  width: 380px;
  height: 500px;
  max-width: 90%; // 改为百分比，更灵活
  max-height: 90vh; // 限制最大高度
  background: white;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  transform: translateX(30vw);
  margin-top: 20px;
}

.register-form-container {
  width: 100%;
  position: relative;
  z-index: 2;
  max-width: 700px;
  padding: 0;

  .register-card {
    width: 100%;
    overflow: hidden;
    box-shadow: none;
    position: relative;
    padding: 0;
    background: white;
    border: none;

    :deep(.el-card__body) {
      padding: 0 !important;
    }
  }
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    color: #409EFF;
    transform: scale(1.1);
  }
}

/* 左右布局 */
.register-body {
  margin-top: 0 !important;
  padding: 0 !important;
  display: flex;
  max-height: 500px;
}

/* 右侧表单区域 */
.register-form-wrapper {
  flex: 1;
  padding: 10px 40px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 500px;
}

/* 注册头部 */
.register-header {
  margin-bottom: 25px;
  text-align: center;

  h3 {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 8px;
    font-weight: 600;
  }
}

/* 注册表单通用样式 */
.register-form {
  min-height: 220px; /* 设置最小高度 */
  margin: 0 !important;
  flex-direction: column;
  justify-content: flex-start; /* 改为顶部对齐 */
}

/* 输入框样式 */
.register-elegant-input {
  :deep(.el-input__wrapper) {
    border-radius: 0;
    padding: 1px 12px;
    box-shadow: 0 0 0 1px #dcdfe6 inset;
    height: 38px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 0 0 1px #c0c4cc inset;
    }

    &.is-focus {
      box-shadow: 0 0 0 1px #409EFF inset;
    }
  }

  :deep(.el-input__prefix) {
    left: 8px;
    color: #606266;
  }

  :deep(.el-input__suffix) {
    display: flex;
    align-items: center;
    padding-right: 8px;
  }

  :deep(.el-input__inner) {
    padding-right:90px;
  }

  .captcha-btn {
    padding: 0;
    height: auto;
    color: #373737;
    font-size: 13px;
    white-space: nowrap;
    margin-left: 4px;

    &:hover {
      color: #66b1ff;
    }

    &:disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }

  :deep(input) {
    font-size: 14px;
    height: 16px;
    line-height: 36px;

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: #606266 !important;
      -webkit-box-shadow: 0 0 0px 1000px white inset !important;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
}

.register-btn {
  width: 100%;
  margin-top: 8px;
  border-radius: 0;
  padding: 10px 16px;
  font-weight: 600;
  letter-spacing: 6px;
  font-size: 14px;
  transition: all 0.3s;
  box-shadow: 0 3px 8px rgba(64, 158, 255, 0.3);
  height: 42px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(64, 158, 255, 0.4);
  }
}

/* 登录链接样式 */
.login-link {
  text-align: center;
  font-size: 14px;
  padding-bottom: 15px;
  margin-top: 5px;
  border-bottom: 2px solid #d9d9d9;

  span {
    color: #606266;
    vertical-align: middle;
  }

  .el-link {
    font-size: 14px;
    vertical-align: middle;
  }
}

/* 协议复选框样式 */
.agreement {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 20px;
  line-height: 1.6;

  .agreement-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;

    .agree-checkbox {
      margin-right: 4px;

      // 防止点击复选框标签时触发链接
      :deep(.el-checkbox__label) {
        cursor: default;
        user-select: none;
      }
    }

    .agreement-links {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .el-link {
      font-size: 12px;
      vertical-align: baseline;

      &:hover {
        color: #409EFF;
      }
    }

    span {
      color: #909399;
      font-size: 12px;
    }
  }
}

/* 表单项目间距优化 */
:deep(.el-form-item) {
  margin-bottom: 12px;
}

.footer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0;
  color: #7f8c8d;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: auto;

  .copyright {
    margin-bottom: 6px;
  }

  .footer-links {
    display: flex;
    align-items: center;

    .el-divider {
      background-color: #c0c4cc;
      margin: 0 10px;
    }

    .el-link {
      font-size: 12px;
    }
  }
}

.wechat-register-container {
  height: 200px;
  margin: 0 !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;

  .wechat-qrcode {
    text-align: center;
    margin-bottom: 20px;

    img {
      width: 200px;
      height: 200px;
      display: block;
      margin: 0 auto;
      border: 1px solid #f0f0f0;
    }

    .tip {
      margin-top: 15px;
      color: #606266;
      font-size: 14px;
      text-align: center;
    }
  }

  .loading-qrcode {
    padding: 60px 0;
    color: #909399;
    font-size: 14px;
    text-align: center;
  }
}

/* 弹窗样式 */
.policy-dialog {
  .el-dialog {
    width: 50% !important ;
    border-radius: 12px;
    max-height: 80vh; /* 减小高度 */
    display: flex;
    flex-direction: column;
    margin-top: 8vh !important;

    .el-dialog__header {
      padding: 16px 20px;
      margin: 0;
      border-bottom: 1px solid #e6e6e6;

      .dialog-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .el-dialog__body {
      padding: 20px;
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .el-dialog__footer {
      padding: 12px 20px;
      border-top: 1px solid #e6e6e6;
    }
  }
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  line-height: 1.6;
  color: #606266;
  padding-right: 10px;
  max-height: 70vh; /* 限制内容区域高度 */

  h2 {
    color: #2c3e50;
    margin-top: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    font-size: 1.4em;
  }

  h3 {
    color: #34495e;
    margin-top: 18px;
    margin-bottom: 10px;
    font-size: 1.2em;
  }

  p, ul {
    margin-bottom: 12px;
    font-size: 14px;
  }

  ul {
    padding-left: 18px;

    li {
      margin-bottom: 6px;
    }
  }

  strong {
    color: #2c3e50;
  }
}

.register-footer {
  color: #333;
  padding: 0;
  margin-top: auto;
  flex: 1;
  position: relative;

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .footer-sections {
    display: grid;
    grid-template-columns: 2fr 2fr;
    gap: 40px;
    margin-bottom: 30px;
    padding: 10px;
    border-bottom: 1px solid rgb(1, 3, 11);
  }

  .footer-section:first-child {
    padding-right: 20px;
  }

  .footer-logo {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }

  .footer-logo .logo-img {
    height: 40px;
    width: auto;
    margin-right: 12px;
  }

  .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: #01030b;
    background: linear-gradient(45deg, #ff7e5f, #feb47b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .footer-desc {
    color: rgb(1, 3, 11);
    line-height: 1.6;
    font-size: 0.95rem;
    margin: 0;
  }

  .section-title {
    color: #01030b;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 20px;
    position: relative;
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(45deg, #ff7e5f, #feb47b);
    border-radius: 1px;
  }

  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .footer-links li {
    margin-bottom: 12px;
  }

  .footer-links a {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    display: inline-block;
  }

  .footer-links a:hover {
    color: #ff7e5f;
    transform: translateX(5px);
  }

  .contact-info {
    display: flex;
    flex-direction: row;
    gap: 20px;
    flex-wrap: wrap;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgb(1, 3, 11);
    font-size: 0.9rem;
  }

  .contact-item .el-icon {
    color: #ff7e5f;
    font-size: 1.1rem;
  }

  /* 底部版权区域 */
  .footer-bottom {
    margin-top: -10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .copyright-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 200px;
  }

  .copyright-text {
    color: rgb(1, 3, 11);
    font-size: 0.9rem;
  }

  .legal-links {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .legal-link {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.85rem;
    transition: color 0.3s ease;
  }

  .legal-link:hover {
    color: #ff7e5f;
    cursor: pointer !important;
  }

  .divider {
    color: rgb(1, 3, 11);
    font-size: 0.8rem;
  }

  .certification-info {
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  .beian-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .beian-icon {
    height: 16px;
    width: auto;
  }

  .beian-link, .icp-link {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.8rem;
    transition: color 0.3s ease;
  }

  .beian-link:hover, .icp-link:hover {
    color: #ff7e5f;
  }
}


// 笔记本屏幕适配 (762px - 1600px)
@media (max-width: 1600px) and (min-width: 762px) {
  .register-wrapper {
    width:290px;
    height: 380px;
    transform: translateX(30vw);
  }

  .register-form-wrapper {
    padding: 15px; // 减少内边距
  }

  .register-header {
    margin-bottom: 15px;

    h3 {
      font-size: 18px; // 减小标题字体
      margin-bottom: 5px;
    }
  }

  .register-form {
    min-height: 150px;
    margin: 0 !important;
    flex-direction: column;
    justify-content: center;
  }

  // 调整表单元素大小
  :deep(.el-form-item) {
    margin-bottom: 10px;
  }

  .register-elegant-input {
    :deep(.el-input__wrapper) {
      height: 28px; // 减小输入框高度
      padding: 1px 8px;
    }

    :deep(.el-input__inner) {
      font-size: 11px; // 减小输入文字
      padding-right: 70px; // 调整按钮空间
    }

    .captcha-btn {
      font-size: 11px; // 验证码按钮文字缩小
    }
  }

  // 调整注册按钮
  .register-btn {
    height: 32px;
    padding: 8px 12px;
    font-size: 13px;
    letter-spacing: 4px;
    margin-top: 5px;
  }

  // 调整登录链接
  .login-link {
    font-size: 11px;
    padding-bottom: 10px;
    margin-top: 3px;

    .el-link {
      font-size: 11px;
      vertical-align: middle;
    }
  }

  // 调整协议文字
  .agreement {
    font-size: 10px !important; // 进一步缩小字体
    margin-top: 8px;

    .agreement-content {
      display: flex;
      flex-wrap: nowrap !important; // 强制不换行
      align-items: center;
      justify-content: center;
      gap: 2px !important; // 减少间隙
      white-space: nowrap; // 防止文字换行
    }

    .agree-checkbox {
      margin-right: 2px !important;

      :deep(.el-checkbox__label) {
        font-size: 10px !important;
        white-space: nowrap;
      }
    }

    .agreement-links {
      display: flex;
      align-items: center;
      gap: 2px !important;
      white-space: nowrap;
    }

    .el-link {
      font-size: 10px !important;
      white-space: nowrap;
    }

    span {
      font-size: 10px !important;
      white-space: nowrap;
    }
  }

  // 调整错误提示
  .error-message,
  .success-message {
    font-size: 11px;
    height: 18px;

    .el-icon {
      font-size: 12px;
      margin-right: 3px;
    }
  }

  // 调整关闭按钮
  .close-btn {
    font-size: 16px;
    top: 8px;
    right: 8px;
  }
}

@media (max-width: 761px) {
  .register-wrapper {
    flex-direction: column;
    width: 95%;
  }

  .register-body {
    flex-direction: column;
  }

  .register-form-wrapper {
    padding: 20px;
  }
}

@media (max-width: 576px) {
  .register-form-wrapper {
    padding: 15px;
  }

  .register-header h3 {
    font-size: 20px;
  }
}
</style>